import { parseLocaleNumber } from "./decimal";

const MASS_UNIT_FACTORS_TO_KG = {
  kg: 1,
  kilogram: 1,
  kilograms: 1,
  kilogramm: 1,
  g: 0.001,
  gr: 0.001,
  gram: 0.001,
  grams: 0.001,
  gramm: 0.001,
  mg: 0.000001,
};

const VOLUME_UNIT_FACTORS_TO_L = {
  l: 1,
  liter: 1,
  liters: 1,
  litre: 1,
  litres: 1,
  ml: 0.001,
  cl: 0.01,
  dl: 0.1,
};

const RECIPE_UNIT_ALIASES = {
  kg: "kg",
  kilogram: "kg",
  kilograms: "kg",
  kilogramm: "kg",
  g: "g",
  gr: "g",
  gram: "g",
  grams: "g",
  gramm: "g",
  mg: "mg",
  l: "L",
  liter: "L",
  liters: "L",
  litre: "L",
  litres: "L",
  ml: "ml",
  cl: "cl",
  dl: "dl",
};

export const CANONICAL_INVENTORY_UNIT_OPTIONS = ["kg", "L", "unit", "PG", "Stk"];

function cleanUnit(unit) {
  return `${unit ?? ""}`.trim();
}

function unitKey(unit) {
  return cleanUnit(unit).toLowerCase().replace(/\./g, "");
}

function isMassUnitKey(key) {
  return Object.hasOwn(MASS_UNIT_FACTORS_TO_KG, key);
}

function isVolumeUnitKey(key) {
  return Object.hasOwn(VOLUME_UNIT_FACTORS_TO_L, key);
}

export function normalizeRecipeUnit(unit) {
  const cleaned = cleanUnit(unit);
  return RECIPE_UNIT_ALIASES[unitKey(cleaned)] || cleaned;
}

export function normalizeInventoryUnit(unit) {
  const cleaned = cleanUnit(unit);
  const key = unitKey(cleaned);
  if (isMassUnitKey(key)) return "kg";
  if (isVolumeUnitKey(key)) return "L";
  return cleaned;
}

export function convertInventoryQuantityToBase(quantity, unit) {
  const numericQuantity = parseLocaleNumber(quantity);
  if (!Number.isFinite(numericQuantity)) return null;

  const key = unitKey(unit);
  if (isMassUnitKey(key)) {
    return numericQuantity * MASS_UNIT_FACTORS_TO_KG[key];
  }
  if (isVolumeUnitKey(key)) {
    return numericQuantity * VOLUME_UNIT_FACTORS_TO_L[key];
  }
  return numericQuantity;
}

export function convertInventoryCostPerUnitToBase(costPerUnit, unit) {
  const numericCost = parseLocaleNumber(costPerUnit);
  if (!Number.isFinite(numericCost)) return null;

  const key = unitKey(unit);
  if (isMassUnitKey(key)) {
    return numericCost / MASS_UNIT_FACTORS_TO_KG[key];
  }
  if (isVolumeUnitKey(key)) {
    return numericCost / VOLUME_UNIT_FACTORS_TO_L[key];
  }
  return numericCost;
}

export function convertQuantityBetweenUnits(quantity, fromUnit, toUnit) {
  const numericQuantity = parseLocaleNumber(quantity);
  if (!Number.isFinite(numericQuantity)) return 0;

  const normalizedFromUnit = normalizeRecipeUnit(fromUnit);
  const normalizedToUnit = normalizeRecipeUnit(toUnit);
  if (!normalizedFromUnit || !normalizedToUnit || normalizedFromUnit === normalizedToUnit) {
    return numericQuantity;
  }

  const fromMassKey = unitKey(normalizedFromUnit);
  const toMassKey = unitKey(normalizedToUnit);
  const fromIsMass = isMassUnitKey(fromMassKey);
  const toIsMass = isMassUnitKey(toMassKey);
  const fromIsVolume = isVolumeUnitKey(fromMassKey);
  const toIsVolume = isVolumeUnitKey(toMassKey);

  if (fromIsMass && toIsMass) {
    return numericQuantity
      * MASS_UNIT_FACTORS_TO_KG[fromMassKey]
      / MASS_UNIT_FACTORS_TO_KG[toMassKey];
  }

  if (fromIsVolume && toIsVolume) {
    return numericQuantity
      * VOLUME_UNIT_FACTORS_TO_L[fromMassKey]
      / VOLUME_UNIT_FACTORS_TO_L[toMassKey];
  }

  if ((fromIsMass && toIsVolume) || (fromIsVolume && toIsMass)) {
    return fromIsMass
      ? numericQuantity * MASS_UNIT_FACTORS_TO_KG[fromMassKey]
      : numericQuantity * VOLUME_UNIT_FACTORS_TO_L[fromMassKey];
  }

  return 0;
}

export function convertQuantityToInventoryUnit(quantity, recipeUnit, inventoryUnit) {
  return convertQuantityBetweenUnits(quantity, recipeUnit, inventoryUnit);
}
